import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type ServiceNumberAuthStatus = 'Enable' | 'Disable';

export interface ServiceNumberAuthMobile {
  id: string;
  tenantId?: string;
  serviceNumberId?: string;
  countryCode: string;
  mobile: string;
  remark?: string;
  status: ServiceNumberAuthStatus;
  createTime?: number;
  updateTime?: number;
}

interface ParsedMobile {
  countryCode: string;
  mobile: string;
}

@Component({
  selector: 'servicenumber-auth',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzCardModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzPaginationModule,
    NzPopconfirmModule,
    NzSpinModule,
    NzSwitchModule,
    NzTableModule,
    NzTagModule,
    NzToolTipModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './servicenumber-auth.component.html',
  styleUrls: ['./servicenumber-auth.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceNumberAuthComponent implements OnInit {
  @Input() serviceNumberId = '';
  @Input() serviceNumberName = '';
  @Input() capabilityEnabled = false;

  private readonly http = inject(HttpService);
  private readonly msg = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  authForm = this.fb.group({
    mobiles: ['', [Validators.required]],
    remark: ['']
  });

  listOfData: ServiceNumberAuthMobile[] = [];
  selectedIds = new Set<string>();
  loading = false;
  saving = false;
  pageIndex = 1;
  pageSize = 20;
  total = 0;
  key = '';
  currentLoginTenantType = '';
  entryPageUrl = '';
  entryPageUrlLoading = false;

  get authDesc(): string {
    return this.i18n.fanyi('servicenumber.auth.desc').replace(/\{serviceName\}/g, this.serviceNumberName || '-');
  }

  ngOnInit(): void {
    this.initCurrentLoginTenantType();
    if (this.canViewAuthSection() && this.serviceNumberId) {
      this.loadEntryPageUrl();
      this.loadData();
    }
  }

  canViewAuthSection(): boolean {
    return this.currentLoginTenantType === 'Common' && this.capabilityEnabled;
  }

  loadData(): void {
    if (!this.canViewAuthSection() || !this.serviceNumberId) return;
    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_AUTH_LIST, {
        serviceNumberId: this.serviceNumberId,
        pageIndex: this.pageIndex - 1,
        pageSize: this.pageSize,
        key: this.key,
        orderBy: 'updateTime',
        direction: 'desc'
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.listOfData = res.data?.items ?? [];
          this.total = Number(res.data?.count ?? res.data?.totalCount ?? this.listOfData.length);
          this.selectedIds.clear();
        } else {
          this.msg.error(res?.msg ?? this.i18n.fanyi('servicenumber.auth.load-failed'));
        }
        this.cdr.detectChanges();
      });
  }

  loadEntryPageUrl(): void {
    if (!this.canViewAuthSection() || !this.serviceNumberId) {
      this.entryPageUrl = '';
      return;
    }

    this.entryPageUrlLoading = true;
    this.http
      .post(ConstantUtil.TENANT_SERVICENUMBER_AUTH_CONFIG_PAGE_URL, {
        serviceNumberId: this.serviceNumberId
      })
      .pipe(
        finalize(() => {
          this.entryPageUrlLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.entryPageUrl = this.extractEntryPageUrl(res.data);
        } else {
          this.entryPageUrl = '';
        }
        this.cdr.detectChanges();
      });
  }

  copyEntryPageUrl(): void {
    if (this.entryPageUrlLoading) {
      return;
    }

    if (!this.entryPageUrl) {
      this.msg.warning(this.i18n.fanyi('servicenumber.auth.copy-entry-unavailable'));
      return;
    }

    this.copyText(this.entryPageUrl);
  }

  saveMobiles(): void {
    if (!this.canViewAuthSection()) {
      return;
    }
    if (this.authForm.invalid || !this.serviceNumberId) {
      this.authForm.markAllAsTouched();
      this.msg.warning(this.i18n.fanyi('servicenumber.auth.invalid-mobile'));
      return;
    }

    const parsed = this.parseMobiles(this.authForm.value.mobiles || '');
    if (!parsed.length) {
      this.msg.warning(this.i18n.fanyi('servicenumber.auth.invalid-mobile'));
      return;
    }

    const remark = String(this.authForm.value.remark || '').trim();
    this.saving = true;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_AUTH_SAVE, {
        serviceNumberId: this.serviceNumberId,
        items: parsed.map(item => ({ ...item, remark }))
      })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          const failCount = Number(res.data?.failCount ?? 0);
          if (failCount > 0) {
            this.msg.warning(
              res.data?.items?.find((item: any) => !item.success)?.errorMsg ?? this.i18n.fanyi('servicenumber.auth.partial-save')
            );
          } else {
            this.msg.success(this.i18n.fanyi('servicenumber.auth.save-success'));
            this.authForm.patchValue({ mobiles: '', remark: '' });
          }
          this.pageIndex = 1;
          this.loadData();
        } else {
          this.msg.error(res?.msg ?? this.i18n.fanyi('servicenumber.auth.save-failed'));
        }
      });
  }

  setStatus(item: ServiceNumberAuthMobile, enabled: boolean): void {
    if (!this.canViewAuthSection()) {
      return;
    }
    const status: ServiceNumberAuthStatus = enabled ? 'Enable' : 'Disable';
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_AUTH_STATUS_SET, {
        id: item.id,
        status
      })
      .subscribe(res => {
        if (res?.success) {
          item.status = status;
          this.msg.success(this.i18n.fanyi('servicenumber.auth.status-success'));
          this.cdr.detectChanges();
        } else {
          this.msg.error(res?.msg ?? this.i18n.fanyi('servicenumber.auth.status-failed'));
          this.loadData();
        }
      });
  }

  confirmDelete(ids: string[]): void {
    if (!ids.length) return;
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.auth.delete-confirm-title'),
      nzContent: this.i18n.fanyi('servicenumber.auth.delete-confirm-content'),
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOkDanger: true,
      nzOnOk: () => this.deleteRecords(ids)
    });
  }

  deleteRecords(ids: string[]): void {
    if (!this.canViewAuthSection()) {
      return;
    }
    if (!this.serviceNumberId || !ids.length) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_AUTH_DELETE_BATCH, {
        serviceNumberId: this.serviceNumberId,
        ids
      })
      .subscribe(res => {
        if (res?.success) {
          this.msg.success(this.i18n.fanyi('servicenumber.auth.delete-success'));
          ids.forEach(id => this.selectedIds.delete(id));
          this.loadData();
        } else {
          this.msg.error(res?.msg ?? this.i18n.fanyi('servicenumber.auth.delete-failed'));
        }
      });
  }

  onPageIndexChange(index: number): void {
    if (!this.canViewAuthSection()) {
      return;
    }
    this.pageIndex = index;
    this.loadData();
  }

  onSearch(value: string): void {
    if (!this.canViewAuthSection()) {
      return;
    }
    this.key = String(value || '').trim();
    this.pageIndex = 1;
    this.loadData();
  }

  onSelect(id: string, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
  }

  onSelectAll(checked: boolean): void {
    this.selectedIds.clear();
    if (checked) {
      this.listOfData.forEach(item => this.selectedIds.add(item.id));
    }
  }

  private initCurrentLoginTenantType(): void {
    try {
      const currentTenantRaw = localStorage.getItem('current_tenant');
      if (!currentTenantRaw) {
        this.currentLoginTenantType = '';
        return;
      }
      const currentTenant = JSON.parse(currentTenantRaw);
      this.currentLoginTenantType = currentTenant?.type ?? '';
    } catch {
      this.currentLoginTenantType = '';
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  get allChecked(): boolean {
    return this.listOfData.length > 0 && this.listOfData.every(item => this.selectedIds.has(item.id));
  }

  get indeterminate(): boolean {
    return this.selectedIds.size > 0 && !this.allChecked;
  }

  get selectedIdList(): string[] {
    return Array.from(this.selectedIds);
  }

  getStatusColor(status: string): string {
    return status === 'Enable' ? 'green' : 'default';
  }

  getStatusLabel(status: string): string {
    return this.i18n.fanyi(status === 'Enable' ? 'servicenumber.auth.status.enable' : 'servicenumber.auth.status.disable');
  }

  trackById(index: number, item: ServiceNumberAuthMobile): string {
    return item.id;
  }

  private extractEntryPageUrl(data: unknown): string {
    if (!data || typeof data !== 'object') {
      return '';
    }

    const url = (data as { url?: unknown }).url;
    return typeof url === 'string' ? url.trim() : '';
  }

  private copyText(value: string): void {
    if (!value) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => this.msg.success(this.i18n.fanyi('common.copy-success')))
        .catch(() => this.fallbackCopy(value));
      return;
    }

    this.fallbackCopy(value);
  }

  private fallbackCopy(value: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        this.msg.success(this.i18n.fanyi('common.copy-success'));
      } else {
        this.msg.error(this.i18n.fanyi('common.copy-failed'));
      }
    } catch {
      this.msg.error(this.i18n.fanyi('common.copy-failed'));
    }
  }

  private parseMobiles(raw: string): ParsedMobile[] {
    const seen = new Set<string>();
    return String(raw || '')
      .split(/[\n,，;；]+/)
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => this.normalizeMobile(item))
      .filter((item): item is ParsedMobile => !!item)
      .filter(item => {
        const key = `${item.countryCode}:${item.mobile}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  private normalizeMobile(raw: string): ParsedMobile | null {
    const value = raw.replace(/[()\-\s]/g, '');
    const mainland = value.match(/^\+86(\d+)$/);
    if (mainland && /^1[3-9]\d{9}$/.test(mainland[1])) {
      return { countryCode: '+86', mobile: mainland[1] };
    }
    const taiwan = value.match(/^\+886(\d+)$/);
    const countryCode = '+886';
    const mobile = taiwan ? taiwan[1] : value.replace(/^\+/, '');

    if (/^0?9\d{8}$/.test(mobile)) {
      return { countryCode, mobile: mobile.replace(/^0/, '') };
    }
    return null;
  }
}
